import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PublicLayout } from '../../components/PublicLayout';
import { publicAPI, imagesAPI } from '../../lib/api';
import { useBooking } from '../../contexts/BookingContext';
import { getCurrencySymbol } from '../../lib/currencies';
import { ImageGallery } from '../../components/ImageGallery';
import { ReservationSearch } from '../../components/ReservationSearch';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { format } from 'date-fns';
import {
  Building2,
  MapPin,
  Mail,
  Users,
  BedDouble,
  Check,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';

export default function PropertyBookingPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { updateSearch, setSearchResults, setSelectedRoom } = useBooking();

  const [property, setProperty] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchResults, setLocalSearchResults] = useState(null);

  useEffect(() => {
    const fetchProperty = async () => {
      try {
        const response = await publicAPI.getPropertyBySlug(slug);
        setProperty(response.data.property);
        setRooms(response.data.rooms);
      } catch (error) {
        console.error('Property not found:', error);
        setError('Property not found');
      } finally {
        setLoading(false);
      }
    };

    fetchProperty();
  }, [slug]);

  const handleSearch = async ({ checkIn, checkOut, guests }) => {
    if (!checkIn || !checkOut) {
      toast.error('Please select check-in and check-out dates');
      return;
    }

    setSearchLoading(true);
    try {
      const searchData = {
        check_in: format(checkIn, 'yyyy-MM-dd'),
        check_out: format(checkOut, 'yyyy-MM-dd'),
        guests: parseInt(guests),
        property_id: property.id,
      };

      const response = await publicAPI.searchAvailability(searchData);
      setLocalSearchResults(response.data);
      updateSearch({
        checkIn: searchData.check_in,
        checkOut: searchData.check_out,
        guests: searchData.guests,
        propertyId: property.id,
      });
    } catch (error) {
      toast.error('Failed to search availability');
    } finally {
      setSearchLoading(false);
    }
  };

  const handleBookRoom = (room) => {
    setSelectedRoom(room);
    setSearchResults({
      ...searchResults,
      rooms: [room],
    });
    navigate('/checkout');
  };

  const resolveImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    return imagesAPI.getUrl(path);
  };

  const getRoomCover = (room) => {
    if (room.gallery?.length > 0) return resolveImageUrl(room.gallery[0]);
    if (room.image_url) return resolveImageUrl(room.image_url);
    return null;
  };

  if (loading) {
    return (
      <PublicLayout>
        <div className="flex items-center justify-center min-h-[50vh]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </PublicLayout>
    );
  }

  if (error || !property) {
    return (
      <PublicLayout>
        <div className="py-24 text-center">
          <Building2 className="h-16 w-16 text-muted-foreground mx-auto mb-6" />
          <h1 className="text-2xl font-semibold mb-4">Property Not Found</h1>
          <p className="text-muted-foreground mb-8">
            The property you're looking for doesn't exist or is not available.
          </p>
          <Button onClick={() => navigate('/properties')}>Browse Properties</Button>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <div data-testid="property-booking-page">
        {/* Property Header */}
        <div className="bg-surface border-b border-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <h1 className="text-3xl md:text-4xl font-serif font-medium mb-2">
              {property.name}
            </h1>
            <div className="flex items-center gap-2 text-muted-foreground">
              <MapPin className="h-5 w-5" />
              <span>{property.address}, {property.city}, {property.country}</span>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Property Gallery */}
          {(property.gallery?.length > 0 || property.image_url) && (
            <div className="mb-10">
              <ImageGallery
                images={property.gallery?.length > 0 ? property.gallery : (property.image_url ? [property.image_url] : [])}
                alt={property.name}
                fallbackIcon={<Building2 className="h-16 w-16 text-muted-foreground" />}
              />
            </div>
          )}

          {/* Search Box */}
          <div className="mb-12">
            <ReservationSearch
              onSearch={handleSearch}
              loading={searchLoading}
              showTitle={true}
              showGuests={true}
              className="max-w-lg"
            />
          </div>

          {/* Property Info */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
            <div className="lg:col-span-2">
              <h2 className="text-2xl font-serif font-medium mb-4">About This Property</h2>
              {property.description ? (
                <p className="text-muted-foreground leading-relaxed">{property.description}</p>
              ) : (
                <p className="text-muted-foreground">
                  Welcome to {property.name}. We look forward to hosting you.
                </p>
              )}
            </div>
            <div className="bg-surface rounded-xl p-6">
              <h3 className="font-semibold mb-4">Contact</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-muted-foreground">
                  <Mail className="h-5 w-5" />
                  <span>{property.contact_email}</span>
                </div>
                <div className="flex items-center gap-3 text-muted-foreground">
                  <MapPin className="h-5 w-5" />
                  <span>{property.city}, {property.country}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Search Results or Default Rooms */}
          <div>
            <h2 className="text-2xl font-serif font-medium mb-6">
              {searchResults ? `Available Rooms (${searchResults.rooms.length})` : 'Our Rooms'}
            </h2>

            {searchResults ? (
              searchResults.rooms.length === 0 ? (
                <div className="bg-surface rounded-xl p-12 text-center">
                  <BedDouble className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No rooms available</h3>
                  <p className="text-muted-foreground">Try different dates.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {searchResults.rooms.map((room) => (
                    <div
                      key={room.id}
                      className="bg-white border border-border rounded-xl p-6 hover:shadow-soft transition-shadow"
                      data-testid={`slug-room-${room.id}`}
                    >
                      <div className="flex flex-col md:flex-row gap-6">
                        <div className="w-full md:w-48 h-36 rounded-lg overflow-hidden flex-shrink-0">
                          {getRoomCover(room) ? (
                            <img
                              src={getRoomCover(room)}
                              alt={room.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full bg-muted flex items-center justify-center">
                              <BedDouble className="h-10 w-10 text-muted-foreground" />
                            </div>
                          )}
                          {room.gallery?.length > 1 && (
                            <div className="mt-1 flex gap-1">
                              {room.gallery.slice(1, 4).map((img, i) => (
                                <div key={i} className="w-14 h-10 rounded overflow-hidden">
                                  <img src={resolveImageUrl(img)} alt="" className="w-full h-full object-cover" loading="lazy" />
                                </div>
                              ))}
                              {room.gallery.length > 4 && (
                                <div className="w-14 h-10 rounded bg-muted flex items-center justify-center text-xs text-muted-foreground">
                                  +{room.gallery.length - 4}
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        <div className="flex-1">
                          <div className="flex items-start justify-between mb-2">
                            <h3 className="text-xl font-semibold">{room.name}</h3>
                            <Badge variant="outline" className="bg-emerald-100 text-emerald-800 border-emerald-200">
                              {room.available_count} available
                            </Badge>
                          </div>

                          {room.description && (
                            <p className="text-muted-foreground mb-4">{room.description}</p>
                          )}

                          <div className="flex items-center gap-4 mb-4">
                            <div className="flex items-center gap-1 text-sm text-muted-foreground">
                              <Users className="h-4 w-4" />
                              <span>Up to {room.max_occupancy} guests</span>
                            </div>
                            {room.amenities?.length > 0 && (
                              <div className="flex items-center gap-2 flex-wrap">
                                {room.amenities.slice(0, 3).map((amenity) => (
                                  <Badge key={amenity} variant="secondary" className="text-xs">
                                    {amenity}
                                  </Badge>
                                ))}
                              </div>
                            )}
                          </div>

                          <div className="flex items-end justify-between">
                            <div>
                              <p className="text-sm text-muted-foreground">
                                {getCurrencySymbol(room.currency || property.currency)}{room.price_per_night} / night
                              </p>
                              <p className="text-2xl font-semibold">
                                {getCurrencySymbol(room.currency || property.currency)}{room.total_price}
                                <span className="text-sm font-normal text-muted-foreground"> total</span>
                              </p>
                            </div>
                            <Button onClick={() => handleBookRoom(room)} data-testid={`slug-book-${room.id}`}>
                              Book Now
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {rooms.map((room) => (
                  <div
                    key={room.id}
                    className="bg-white border border-border rounded-xl overflow-hidden hover:shadow-soft transition-shadow"
                    data-testid={`slug-default-room-${room.id}`}
                  >
                    <div className="aspect-video overflow-hidden">
                      {getRoomCover(room) ? (
                        <img
                          src={getRoomCover(room)}
                          alt={room.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-muted flex items-center justify-center">
                          <BedDouble className="h-10 w-10 text-muted-foreground" />
                        </div>
                      )}
                    </div>
                    {room.gallery?.length > 1 && (
                      <div className="flex gap-1 px-3 pt-2">
                        {room.gallery.slice(1, 5).map((img, i) => (
                          <div key={i} className="w-10 h-8 rounded overflow-hidden">
                            <img src={resolveImageUrl(img)} alt="" className="w-full h-full object-cover" loading="lazy" />
                          </div>
                        ))}
                        {room.gallery.length > 5 && (
                          <div className="w-10 h-8 rounded bg-muted flex items-center justify-center text-[10px] text-muted-foreground">
                            +{room.gallery.length - 5}
                          </div>
                        )}
                      </div>
                    )}
                    <div className="p-4">
                      <h3 className="font-semibold mb-2">{room.name}</h3>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                        <Users className="h-4 w-4" />
                        <span>Up to {room.max_occupancy} guests</span>
                      </div>
                      <p className="text-lg font-semibold text-primary">
                        {getCurrencySymbol(property.currency)}{room.price_per_night}
                        <span className="text-sm font-normal text-muted-foreground"> / night</span>
                      </p>
                    </div>
                  </div>
                ))}
                {rooms.length === 0 && (
                  <div className="col-span-full bg-surface rounded-xl p-12 text-center">
                    <BedDouble className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No rooms available at this property.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}
